"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UrlManager = void 0;
const SEARCH_SORT_DICTIONARY = new Set(['price-asc', 'price-desc', 'rating', 'discount', 'most-offers', 'latest', 'most-relevant']);
const CATEGORY_SORT_DICTIONARY = new Set(['price-asc', 'price-desc', 'rating', 'discount']);
const PRODUCT_SORT_DICTIONARY = new Set(['score', 'price-desc', 'price-asc', 'shop-score']);
const PRODUCT_FILTERS_DICTIONARY = new Set([
    'badge_is_authorized',
    'badge_opineo_certificate',
    'badge_free_delivery',
    'badge_rates',
    'badge_fast_payments',
    'badge_delivery_24h',
    'badge_personal_receipt',
    'in_post'
]);
class UrlManager {
    static renderQuery(queries) {
        if (queries && Object.keys(queries).length) {
            const query = Object.entries(queries)
                .map(([key, value]) => (`${key}=${typeof value === 'boolean' ? Number(value) : encodeURIComponent(value)}`))
                .join('&');
            return `?${query}`;
        }
        return '';
    }
    static filterQueryParams(query) {
        const restrictedQueryParams = ['filters', 'sort'];
        const filteredQuery = {};
        for (const key in query) {
            if (key === 'filters' && typeof query[key] === 'string') {
                const filters = query[key]
                    .split(',')
                    .filter(filter => PRODUCT_FILTERS_DICTIONARY.has(filter));
                if (filters.length) {
                    filteredQuery[key] = filters.join(',');
                }
            }
            if (key === 'sort') {
                const sortQuery = query[key];
                if (PRODUCT_SORT_DICTIONARY.has(sortQuery)) {
                    filteredQuery[key] = sortQuery;
                    if (sortQuery === 'score') {
                        delete filteredQuery.sort;
                    }
                }
            }
            if (!restrictedQueryParams.includes(key)) {
                filteredQuery[key] = query[key];
            }
        }
        return filteredQuery;
    }
}
exports.UrlManager = UrlManager;
UrlManager.category = {
    makeNumericFiltersPart: ({ dealerId, filters }) => {
        const filterElements = [];
        if (filters) {
            for (const filterId of Object.keys(filters).sort((a, b) => parseInt(a) - parseInt(b))) {
                const filterValue = filters[filterId];
                const filterValues = Array.isArray(filterValue)
                    ? [...filterValue.values()]
                        .filter((value, index, self) => self.indexOf(value) === index)
                        .sort((a, b) => a - b)
                        .join('.')
                    : `${filterValue.from || 0}-${filterValue.to || 0}`;
                filterElements.push(`${filterId}.${filterValues}`);
            }
        }
        if (dealerId) {
            filterElements.push(`9999.${dealerId}`);
        }
        return filterElements.join(',');
    },
    makeSluggedFiltersPart: (filters) => {
        const filterElements = [];
        if (filters.length) {
            filters.sort((a, b) => a.position - b.position)
                .forEach(filter => {
                const filterValues = filter.entries
                    .sort((a, b) => a.position - b.position)
                    .map(entry => entry.slug)
                    .join(',');
                filterElements.push(`${filter.slug}:${filterValues}`);
            });
        }
        return filterElements.join('/');
    },
    ranking: ({ id, slug, query }) => UrlManager.category.view({ id, slug, query }).replace('.html', '/ranking.html'),
    view: ({ id, dealerId, useSlugs = false, filters = useSlugs === true ? [] : {}, location: { id: locationId, slug: locationSlug } = {}, page = 1, slug, sort, price, productId, query = {}, customUrl }) => {
        if (!id) {
            throw new Error('Bad params for URL!');
        }
        const searchQueryObject = Object.keys(query);
        let searchQuery = '';
        if (searchQueryObject.length !== 0) {
            searchQuery = `?${searchQueryObject.map(key => `${key}=${encodeURIComponent(query[key])}`).join('&')}`;
        }
        const categoryType = id[0];
        let prefix = '/cat';
        switch (categoryType) {
            case 'c':
                prefix = '/cat';
                break;
            case 'b':
                prefix = '/sub';
                break;
            case 'a':
                prefix = '/dep';
                break;
        }
        if (Object.keys(filters).length === 0
            && !dealerId
            && !locationId
            && page < 2
            && (!sort)
            && slug
            && (price === undefined || ((price === null || price === void 0 ? void 0 : price.min) === 0 && (price === null || price === void 0 ? void 0 : price.max) === 0))
            && (!productId)) {
            if (customUrl) {
                return `/${customUrl.replace(/^\//, '')}${searchQuery}`;
            }
            return `${prefix}/${id.substring(1)}-${slug}.html${searchQuery}`;
        }
        const categoryPart = useSlugs && slug ? `${id.substring(1)}-${slug}` : id.substring(1).toString();
        const urlElements = [prefix, categoryPart];
        const filterElements = useSlugs
            ? UrlManager.category.makeSluggedFiltersPart(filters)
            : UrlManager.category.makeNumericFiltersPart({ dealerId, filters: filters });
        if (filterElements) {
            urlElements.push(filterElements);
        }
        if (sort) {
            if (CATEGORY_SORT_DICTIONARY.has(sort)) {
                urlElements.push(`sort/${sort}`);
            }
        }
        if (locationId && locationSlug) {
            urlElements.push(`${locationId},${locationSlug}`);
        }
        if (page > 1) {
            urlElements.push(`page/${page}`);
        }
        if (price === null || price === void 0 ? void 0 : price.min) {
            urlElements.push(`cena_od/${price === null || price === void 0 ? void 0 : price.min}`);
        }
        if (price === null || price === void 0 ? void 0 : price.max) {
            urlElements.push(`cena_do/${price === null || price === void 0 ? void 0 : price.max}`);
        }
        if (productId) {
            urlElements.push(`product/${productId}`);
        }
        return `${urlElements.join('/')}${searchQuery}`;
    }
};
UrlManager.search = {
    view: ({ categoryId, query, page = 1, sort }) => {
        if (page < 2 && (!sort) && (!categoryId)) {
            return UrlManager.renderQuery({
                query
            });
        }
        const urlElements = {
            query
        };
        if (sort) {
            if (SEARCH_SORT_DICTIONARY.has(sort)) {
                urlElements.sort = sort;
            }
        }
        if (page > 1) {
            urlElements.page = page;
        }
        if (categoryId) {
            urlElements.categoryId = categoryId;
        }
        return UrlManager.renderQuery(urlElements);
    }
};
UrlManager.dealer = {
    view: ({ dealerId }) => `/site/sklep/${dealerId}`
};
UrlManager.producer = {
    prefix: '/producenci',
    products: ({ name, categoryId }) => (`${UrlManager.producer.prefix}/${encodeURIComponent(name.toLowerCase())}/produkty${categoryId ? `/cat-${categoryId}` : ''}`)
};
UrlManager.producerPage = {
    view: ({ categoryId, dealerId, filters = {}, page = 1, categorySlug, producerSlug, sort, price, query = {} }) => {
        if (!producerSlug) {
            throw new Error('Bad params for URL!');
        }
        const searchQueryObject = Object.keys(query);
        const urlElements = ['', 'strefa-producenta', producerSlug];
        let searchQuery = '';
        if (searchQueryObject.length !== 0) {
            searchQuery = `?${searchQueryObject.map(key => `${key}=${encodeURIComponent(query[key])}`).join('&')}`;
        }
        if (Object.keys(filters).length === 0
            && !dealerId
            && page < 2
            && (!sort)
            && !categoryId
            && (price === undefined
                || ((price === null || price === void 0 ? void 0 : price.min) === 0 && (price === null || price === void 0 ? void 0 : price.max) === 0))) {
            return `${urlElements.join('/')}${searchQuery}`;
        }
        let prefix = 'cat';
        const categoryType = categoryId ? categoryId[0] : null;
        switch (categoryType) {
            case 'c':
                prefix = 'cat';
                break;
            case 'b':
                prefix = 'sub';
                break;
            case 'a':
                prefix = 'dep';
                break;
        }
        if (Object.keys(filters).length === 0
            && !dealerId
            && page < 2
            && (!sort)
            && categoryId
            && categorySlug
            && (price === undefined
                || ((price === null || price === void 0 ? void 0 : price.min) === 0 && (price === null || price === void 0 ? void 0 : price.max) === 0))) {
            return `${urlElements.join('/')}/${prefix}/${categoryId.substring(1)}-${categorySlug}.html${searchQuery}`;
        }
        if (categoryId) {
            urlElements.push(prefix, categoryId.substring(1).toString());
        }
        const filterElements = UrlManager.category.makeNumericFiltersPart({ dealerId, filters });
        if (filterElements) {
            urlElements.push(filterElements);
        }
        if (sort) {
            if (CATEGORY_SORT_DICTIONARY.has(sort)) {
                urlElements.push(`sort/${sort}`);
            }
        }
        if (page > 1) {
            urlElements.push(`page/${page}`);
        }
        if (price === null || price === void 0 ? void 0 : price.min) {
            urlElements.push(`cena_od/${price === null || price === void 0 ? void 0 : price.min}`);
        }
        if (price === null || price === void 0 ? void 0 : price.max) {
            urlElements.push(`cena_do/${price === null || price === void 0 ? void 0 : price.max}`);
        }
        return `${urlElements.join('/')}${searchQuery}`;
    }
};
UrlManager.product = {
    view: ({ categoryId, productId, query }) => {
        const url = `/site/cat/${categoryId}/comp/${productId}`;
        if (!query || Object.keys(query).length === 0) {
            return url;
        }
        return `${url}${UrlManager.renderQuery(UrlManager.filterQueryParams(query))}`;
    }
};
UrlManager.redirect = {
    shop: ({ dealerId, offerId, productId, query }) => (`/site/red/${dealerId}/${productId}/${offerId}${UrlManager.renderQuery(query)}`)
};
UrlManager.seo = {
    categoryLink: ({ slug }) => (`/oferty/${encodeURIComponent(slug)}`)
};
//# sourceMappingURL=index.js.map